import React, { useState, useEffect } from 'react';
import { adminAPI, newsAPI } from '../../services/api';
import api from '../../services/api';
import './AdminPanel.css';
import RichTextEditor, { isRichEmpty, cleanHtml } from '../RichText/RichTextEditor';

const SUBJECTS = [
  'History', 'Geography', 'Polity', 'Economy', 'Environment',
  'Science & Technology', 'Current Affairs', 'Art & Culture',
  'International Relations', 'Social Issues', 'Ethics', 'Governance','Disaster Management', 'Miscellaneous'
];

const EXAMS = [
  'UPSC CSE', 'UPSC CAPF', 'UPSC NDA', 'UPPSC', 'BPSC', 'MPPSC',
  'MPSC', 'TNPSC', 'SSC CGL', 'SSC CHSL', 'RBI Grade B', 'IBPS PO', 'Other'
];

const DEFAULT_MATCH_LISTS = {
  list1Label: 'List I',
  list2Label: 'List II',
  list1Items: [
    { key: 'A', text: '' },
    { key: 'B', text: '' },
    { key: 'C', text: '' },
    { key: 'D', text: '' },
  ],
  list2Items: [
    { key: '1', text: '' },
    { key: '2', text: '' },
    { key: '3', text: '' },
    { key: '4', text: '' },
  ],
};

const EMPTY_PYQ = {
  questionType: 'mcq',
  question: '',
  answer: '',
  statements: ['', '', ''],
  assertion: '',
  reason: '',
  matchLists: DEFAULT_MATCH_LISTS,
  options: [
    { text: '', isCorrect: true },
    { text: '', isCorrect: false },
    { text: '', isCorrect: false },
    { text: '', isCorrect: false },
  ],
  correctAnswer: 0,
  explanation: '',
  subject: 'Polity',
  topic: '',
  exam: 'UPSC CSE',
  year: new Date().getFullYear(),
  paperCode: '',
  keywords: '',
  difficulty: 'medium',
};

const EMPTY_VIDEO = {
  subject: 'History',
  chapter: '',
  title: '',
  description: '',
  youtubeUrl: '',
  duration: '',
  order: 0,
};

/** Extract YouTube video ID for preview. */
function getYouTubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:v=|youtu\.be\/|embed\/)([A-Za-z0-9_-]{11})/);
  return match ? match[1] : null;
}

/** Normalize both modern {key,text} items and legacy string-only items. */
const normalizeQuestionListItems = (items, defaultKeys) => {
  if (!Array.isArray(items) || items.length === 0) {
    return defaultKeys.map(key => ({ key, text: '' }));
  }

  return items.map((item, index) => {
    const fallbackKey = defaultKeys[index] || String(index + 1);

    if (typeof item === 'string') {
      return { key: fallbackKey, text: item };
    }

    return {
      key: String(item?.key || fallbackKey),
      text: typeof item?.text === 'string' ? item.text : '',
    };
  });
};

/** Maps a saved API question back into the form shape. */
const questionToForm = (q) => ({
  questionType: q.questionType || 'mcq',
  question: q.question || '',
  answer: q.answer || q.modelAnswer ||
    (q.questionType === 'mains' ? (q.explanation || '') : ''),
  statements: q.statements?.length ? q.statements : ['', '', ''],
  assertion: q.assertion || '',
  reason: q.reason || '',
  matchLists: q.matchLists
    ? {
        list1Label: q.matchLists.list1Label || 'List I',
        list2Label: q.matchLists.list2Label || 'List II',
        list1Items: normalizeQuestionListItems(
          q.matchLists.list1Items,
          ['A', 'B', 'C', 'D']
        ),
        list2Items: normalizeQuestionListItems(
          q.matchLists.list2Items,
          ['1', '2', '3', '4']
        ),
      }
    : {
        ...DEFAULT_MATCH_LISTS,
        list1Items: DEFAULT_MATCH_LISTS.list1Items.map(item => ({ ...item })),
        list2Items: DEFAULT_MATCH_LISTS.list2Items.map(item => ({ ...item })),
      },
  options: q.options?.length
    ? q.options.map((o, i) => ({
        text: typeof o === 'string' ? o : (o.text || ''),
        isCorrect: i === (q.correctAnswer ?? 0),
      }))
    : [
        { text: '', isCorrect: true },
        { text: '', isCorrect: false },
        { text: '', isCorrect: false },
        { text: '', isCorrect: false },
      ],
  correctAnswer: q.correctAnswer ?? 0,
  explanation: q.explanation || '',
  subject: q.subject || 'Polity',
  topic: q.topic || '',
  exam: q.exam || 'UPSC CSE',
  year: q.year || new Date().getFullYear(),
  paperCode: q.paperCode || '',
  keywords: Array.isArray(q.keywords)
    ? q.keywords.join(', ')
    : (q.keywords || ''),
  difficulty: q.difficulty || 'medium',
});

/** Builds the payload for POST / PUT. */
const buildPYQPayload = (form) => buildPYQPayloadRaw(
  form.questionType === 'mains'
    ? { ...form, question: cleanHtml(form.question), answer: cleanHtml(form.answer) }
    : form
);
const buildPYQPayloadRaw = (form) => ({
  ...form,
  keywords: typeof form.keywords === 'string'
    ? form.keywords.split(',').map(k => k.trim()).filter(Boolean)
    : form.keywords,
  statements: form.questionType === 'statement'
    ? form.statements.filter(s => s.trim())
    : [],
  matchLists: form.questionType === 'match-list'
    ? form.matchLists
    : undefined,

  // modelAnswer is canonical. The aliases retain compatibility.
  modelAnswer: form.questionType === 'mains' ? (form.answer || '').trim() : '',
  answer: form.questionType === 'mains' ? (form.answer || '').trim() : '',
  explanation: form.questionType === 'mains'
    ? (form.answer || '').trim()
    : form.explanation,

  options: ['mcq', 'statement', 'assertion-reason', 'match-list'].includes(
    form.questionType
  )
    ? form.options.map(o => o.text).filter(Boolean)
    : [],

  correctAnswer: parseInt(form.correctAnswer, 10) || 0,
  year: parseInt(form.year, 10) || null,
});

/* ─── Reusable PYQ form ─────────────────────────────────────────────────── */
function PYQFormFields({
  form,
  setForm,
  updateOptFn,
  onSubmit,
  submitLabel,
  isLoading,
}) {
  const updateL1 = (i, val) => {
    const items = [...form.matchLists.list1Items];
    items[i] = { ...items[i], text: val };
    setForm(f => ({
      ...f,
      matchLists: { ...f.matchLists, list1Items: items },
    }));
  };

  const updateL2 = (i, val) => {
    const items = [...form.matchLists.list2Items];
    items[i] = { ...items[i], text: val };
    setForm(f => ({
      ...f,
      matchLists: { ...f.matchLists, list2Items: items },
    }));
  };

  const hasListContent =
    form.matchLists.list1Items.some(i => i.text) ||
    form.matchLists.list2Items.some(i => i.text);

  return (
    <form
      onSubmit={onSubmit}
      style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
    >
      <div style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr 1fr',
        gap: '0.75rem',
      }}>
        <div className="form-group">
          <label className="label">Exam *</label>
          <select
            className="input"
            value={form.exam}
            onChange={e => setForm(f => ({ ...f, exam: e.target.value }))}
          >
            {EXAMS.map(ex => <option key={ex}>{ex}</option>)}
          </select>
        </div>

        <div className="form-group">
          <label className="label">Year</label>
          <input
            className="input"
            type="number"
            min="2000"
            max="2030"
            value={form.year}
            onChange={e => setForm(f => ({ ...f, year: e.target.value }))}
          />
        </div>

        <div className="form-group">
          <label className="label">Paper Code</label>
          <input
            className="input"
            placeholder="e.g. GS Paper 1"
            value={form.paperCode}
            onChange={e => setForm(f => ({ ...f, paperCode: e.target.value }))}
          />
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1.5fr 3fr 1fr',
        gap: '0.75rem',
      }}>
        <div className="form-group">
          <label className="label">Subject *</label>
          <select
            className="input"
            value={form.subject}
            onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
          >
            {SUBJECTS.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>

        <div className="form-group">
          <label className="label">Topic</label>
          <input
            className="input"
            placeholder="e.g. Fundamental Rights, Monetary Policy"
            value={form.topic}
            onChange={e => setForm(f => ({ ...f, topic: e.target.value }))}
          />
        </div>

        <div className="form-group">
          <label className="label">Difficulty</label>
          <select
            className="input"
            value={form.difficulty}
            onChange={e => setForm(f => ({ ...f, difficulty: e.target.value }))}
          >
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>
        </div>
      </div>

      <div className="form-group">
        <label className="label">Question Type *</label>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          {[
            ['mcq', 'MCQ'],
            ['statement', 'Triple Statement'],
            ['assertion-reason', 'Assertion-Reason'],
            ['match-list', '🔀 Match the List'],
            ['mains', '✍️ Mains Written Answer'],
          ].map(([val, lbl]) => (
            <button
              key={val}
              type="button"
              onClick={() => setForm(f => ({ ...f, questionType: val }))}
              style={{
                padding: '.45rem 1rem',
                borderRadius: '8px',
                border: '1px solid',
                cursor: 'pointer',
                fontSize: '.82rem',
                fontWeight: 600,
                transition: 'all .15s',
                borderColor: form.questionType === val
                  ? 'rgba(0,86,210,0.5)'
                  : 'rgba(15,23,42,0.08)',
                background: form.questionType === val
                  ? 'rgba(0,86,210,0.12)'
                  : 'rgba(15,23,42,0.03)',
                color: form.questionType === val ? '#0056D2' : 'var(--text-2)',
              }}
            >
              {lbl}
            </button>
          ))}
        </div>
      </div>

      {form.questionType === 'statement' && (
        <div className="form-group">
          <label className="label">Statements</label>
          {form.statements.map((s, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                gap: '.5rem',
                alignItems: 'flex-start',
                marginBottom: '.5rem',
              }}
            >
              <span style={{
                color: '#0056D2',
                fontWeight: 700,
                minWidth: 20,
                paddingTop: '.55rem',
              }}>
                {i + 1}.
              </span>
              <textarea
                className="input"
                rows={2}
                style={{ flex: 1 }}
                placeholder={`Statement ${i + 1}`}
                value={s}
                onChange={e => {
                  const stmts = [...form.statements];
                  stmts[i] = e.target.value;
                  setForm(f => ({ ...f, statements: stmts }));
                }}
              />
            </div>
          ))}
        </div>
      )}

      {form.questionType === 'assertion-reason' && (
        <div
          className="form-group"
          style={{ display: 'flex', flexDirection: 'column', gap: '.6rem' }}
        >
          <label className="label">Assertion & Reason</label>
          <textarea
            className="input"
            rows={2}
            placeholder="Assertion (A): ..."
            value={form.assertion}
            onChange={e => setForm(f => ({ ...f, assertion: e.target.value }))}
          />
          <textarea
            className="input"
            rows={2}
            placeholder="Reason (R): ..."
            value={form.reason}
            onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
          />
        </div>
      )}

      {form.questionType === 'match-list' && (
        <div className="form-group">
          <label className="label">
            Lists — enter items for both columns
          </label>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem',
            marginTop: '.6rem',
          }}>
            <div style={{
              background: 'rgba(0,86,210,0.05)',
              borderRadius: 12,
              padding: '1rem',
              border: '1px solid rgba(0,86,210,0.18)',
            }}>
              <input
                className="input"
                style={{
                  fontWeight: 700,
                  marginBottom: '.85rem',
                  color: '#0056D2',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '1px solid rgba(0,86,210,0.3)',
                  borderRadius: 0,
                  padding: '.2rem 0',
                  fontSize: '.9rem',
                }}
                value={form.matchLists.list1Label}
                placeholder="List I label"
                onChange={e => setForm(f => ({
                  ...f,
                  matchLists: {
                    ...f.matchLists,
                    list1Label: e.target.value,
                  },
                }))}
              />

              {form.matchLists.list1Items.map((item, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    gap: '.5rem',
                    alignItems: 'flex-start',
                    marginBottom: '.5rem',
                  }}
                >
                  <span style={{
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '.78rem',
                    background: 'rgba(0,86,210,0.3)',
                    borderRadius: '50%',
                    width: 22,
                    height: 22,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: '.45rem',
                  }}>
                    {item.key}
                  </span>
                  <textarea
                    className="input"
                    rows={2}
                    style={{ flex: 1, fontSize: '.83rem' }}
                    placeholder={`Item ${item.key}...`}
                    value={item.text}
                    onChange={e => updateL1(i, e.target.value)}
                  />
                </div>
              ))}
            </div>

            <div style={{
              background: 'rgba(0,86,210,0.05)',
              borderRadius: 12,
              padding: '1rem',
              border: '1px solid rgba(0,86,210,0.2)',
            }}>
              <input
                className="input"
                style={{
                  fontWeight: 700,
                  marginBottom: '.85rem',
                  color: '#0056D2',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '1px solid rgba(0,86,210,0.3)',
                  borderRadius: 0,
                  padding: '.2rem 0',
                  fontSize: '.9rem',
                }}
                value={form.matchLists.list2Label}
                placeholder="List II label"
                onChange={e => setForm(f => ({
                  ...f,
                  matchLists: {
                    ...f.matchLists,
                    list2Label: e.target.value,
                  },
                }))}
              />

              {form.matchLists.list2Items.map((item, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    gap: '.5rem',
                    alignItems: 'flex-start',
                    marginBottom: '.5rem',
                  }}
                >
                  <span style={{
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '.78rem',
                    background: 'rgba(0,86,210,0.3)',
                    borderRadius: '50%',
                    width: 22,
                    height: 22,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: '.45rem',
                  }}>
                    {item.key}
                  </span>
                  <textarea
                    className="input"
                    rows={2}
                    style={{ flex: 1, fontSize: '.83rem' }}
                    placeholder={`Item ${item.key}...`}
                    value={item.text}
                    onChange={e => updateL2(i, e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>

          {hasListContent && (
            <div style={{
              marginTop: '1rem',
              background: 'var(--bg-3)',
              borderRadius: 12,
              padding: '1.1rem',
              border: '1px solid rgba(15,23,42,0.06)',
            }}>
              <p style={{
                fontSize: '.7rem',
                color: 'var(--text-3)',
                marginBottom: '.8rem',
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                fontWeight: 600,
              }}>
                Preview
              </p>

              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '1.5rem',
              }}>
                <div>
                  <p style={{
                    fontWeight: 700,
                    color: '#0056D2',
                    marginBottom: '.5rem',
                    fontSize: '.88rem',
                  }}>
                    {form.matchLists.list1Label}
                  </p>
                  {form.matchLists.list1Items.filter(i => i.text).map(item => (
                    <p
                      key={item.key}
                      style={{
                        fontSize: '.84rem',
                        color: 'var(--text)',
                        marginBottom: '.3rem',
                        lineHeight: 1.5,
                      }}
                    >
                      <span style={{ fontWeight: 700 }}>{item.key}.</span>
                      {' '}{item.text}
                    </p>
                  ))}
                </div>

                <div>
                  <p style={{
                    fontWeight: 700,
                    color: '#0056D2',
                    marginBottom: '.5rem',
                    fontSize: '.88rem',
                  }}>
                    {form.matchLists.list2Label}
                  </p>
                  {form.matchLists.list2Items.filter(i => i.text).map(item => (
                    <p
                      key={item.key}
                      style={{
                        fontSize: '.84rem',
                        color: 'var(--text)',
                        marginBottom: '.3rem',
                        lineHeight: 1.5,
                      }}
                    >
                      <span style={{ fontWeight: 700 }}>{item.key}.</span>
                      {' '}{item.text}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="form-group">
        <label className="label">
          {form.questionType === 'mcq' ? 'Question *' : 'Question Stem *'}
        </label>
        {form.questionType === 'mains' ? (
          <RichTextEditor
            value={form.question}
            onChange={html => setForm(f => ({ ...f, question: html }))}
            toolbarMode="font-only"
          />
        ) : (
          <textarea
            className="input"
            rows={form.questionType === 'mcq' ? 4 : 2}
            placeholder={
              form.questionType === 'match-list'
                ? 'Match List I with List II and select the correct answer using the codes given below:'
                : 'e.g. Which one of the following statements is correct?'
            }
            value={form.question}
            onChange={e => setForm(f => ({ ...f, question: e.target.value }))}
            required
          />
        )}
      </div>

      {form.questionType !== 'mains' && (
        <div className="form-group">
          <label className="label">
            Options A B C D — click the letter to mark correct
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '.6rem',
            marginTop: '.3rem',
          }}>
            {form.options.map((opt, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: '.5rem',
                  alignItems: 'center',
                }}
              >
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, correctAnswer: i }))}
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    border: '1px solid',
                    flexShrink: 0,
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: '.78rem',
                    transition: 'all .15s',
                    borderColor: form.correctAnswer === i
                      ? 'rgba(22,163,74,0.6)'
                      : 'rgba(15,23,42,0.12)',
                    background: form.correctAnswer === i
                      ? 'rgba(22,163,74,0.18)'
                      : 'transparent',
                    color: form.correctAnswer === i ? '#16A34A' : 'var(--text-3)',
                  }}
                >
                  {String.fromCharCode(65 + i)}
                </button>
                <input
                  className="input"
                  style={{ flex: 1 }}
                  placeholder={
                    form.questionType === 'match-list'
                      ? `e.g. A-${i + 1}, B-${i + 2 > 4 ? 1 : i + 2}, C-3, D-4`
                      : `Option ${String.fromCharCode(65 + i)}`
                  }
                  value={opt.text}
                  onChange={e => updateOptFn(i, e.target.value)}
                />
              </div>
            ))}
          </div>
          <p style={{
            fontSize: '.73rem',
            color: 'var(--text-3)',
            marginTop: '.4rem',
          }}>
            🟢 Green circle = correct answer
          </p>
        </div>
      )}

      {form.questionType === 'mains' ? (
        <div className="form-group">
          <label className="label">Model Answer *</label>
          <RichTextEditor
            value={form.answer || ''}
            onChange={html => setForm(f => ({ ...f, answer: html, explanation: html }))}
          />
          <p style={{
            fontSize: '.75rem',
            color: 'var(--text-3)',
            marginTop: '.35rem',
          }}>
            The model answer is saved in its own field and mirrored to explanation for older clients.
          </p>
        </div>
      ) : (
        <div className="form-group">
          <label className="label">Explanation *</label>
          <textarea
            className="input"
            rows={5}
            placeholder={
              form.questionType === 'match-list'
                ? 'Explain the correct matching...'
                : 'Explain why the answer is correct.'
            }
            value={form.explanation}
            onChange={e => setForm(f => ({ ...f, explanation: e.target.value }))}
            required
          />
        </div>
      )}

      <div className="form-group">
        <label className="label">Keywords (comma separated)</label>
        <input
          className="input"
          placeholder="e.g. fundamental rights, article 32, writs"
          value={form.keywords}
          onChange={e => setForm(f => ({ ...f, keywords: e.target.value }))}
        />
      </div>

      <button
        className="btn btn-primary btn-lg"
        type="submit"
        disabled={isLoading}
      >
        {isLoading ? <><span className="spinner" />Saving...</> : submitLabel}
      </button>
    </form>
  );
}

/* ─── Syllabus Video Manager ─────────────────────────────────────────────── */
function SyllabusManager({ showMsg }) {
  const [videoForm, setVideoForm] = useState(EMPTY_VIDEO);
  const [videoList, setVideoList] = useState([]);
  const [videoLoading, setVideoLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [filterSub, setFilterSub] = useState('');
  const [editVideo, setEditVideo] = useState(null);
  const [ytPreviewId, setYtPreviewId] = useState(null);

  useEffect(() => {
    setYtPreviewId(getYouTubeId(videoForm.youtubeUrl));
  }, [videoForm.youtubeUrl]);

  const loadVideos = async (subject = filterSub) => {
    setVideoLoading(true);
    try {
      const params = subject
        ? `?subject=${encodeURIComponent(subject)}`
        : '';
      const res = await api.get(`/syllabus/admin/all${params}`);
      setVideoList(res.data.videos || []);
    } catch {
      showMsg('❌ Failed to load videos', 'error');
    } finally {
      setVideoLoading(false);
    }
  };

  useEffect(() => {
    loadVideos();
  }, []); // eslint-disable-line

  const saveVideo = async (e) => {
    e.preventDefault();
    setSaveLoading(true);

    try {
      const payload = {
        ...videoForm,
        order: parseInt(videoForm.order, 10) || 0,
      };

      if (editVideo) {
        await api.patch(`/syllabus/videos/${editVideo._id}`, payload);
        showMsg('✅ Video updated!');
        setEditVideo(null);
      } else {
        await api.post('/syllabus/videos', payload);
        showMsg('✅ Video added!');
      }

      setVideoForm(EMPTY_VIDEO);
      loadVideos();
    } catch (err) {
      showMsg(
        '❌ ' + (err.response?.data?.message || 'Save failed'),
        'error'
      );
    } finally {
      setSaveLoading(false);
    }
  };

  const startEdit = (v) => {
    setEditVideo(v);
    setVideoForm({
      subject: v.subject,
      chapter: v.chapter,
      title: v.title,
      description: v.description || '',
      youtubeUrl: v.youtubeUrl,
      duration: v.duration || '',
      order: v.order || 0,
    });

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditVideo(null);
    setVideoForm(EMPTY_VIDEO);
  };

  const deleteVideo = async (id) => {
    if (!window.confirm('Delete this video?')) return;

    try {
      await api.delete(`/syllabus/videos/${id}`);
      showMsg('✅ Deleted');
      loadVideos();
    } catch {
      showMsg('❌ Delete failed', 'error');
    }
  };

  const grouped = videoList.reduce((acc, v) => {
    const key = `${v.subject}|||${v.chapter}`;
    if (!acc[key]) {
      acc[key] = { subject: v.subject, chapter: v.chapter, videos: [] };
    }
    acc[key].videos.push(v);
    return acc;
  }, {});

  return (
    <div>
      <div className="admin-card">
        <h3 className="admin-section-title">
          {editVideo ? '✏️ Edit Video' : '➕ Add Video Lecture'}
        </h3>

        <form
          onSubmit={saveVideo}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
          }}
        >
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 2fr',
            gap: '0.75rem',
          }}>
            <div className="form-group">
              <label className="label">Subject *</label>
              <select
                className="input"
                value={videoForm.subject}
                onChange={e => setVideoForm(f => ({
                  ...f,
                  subject: e.target.value,
                }))}
              >
                {SUBJECTS.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label className="label">Chapter *</label>
              <input
                className="input"
                placeholder="e.g. Ancient India, Indus Valley Civilization"
                value={videoForm.chapter}
                onChange={e => setVideoForm(f => ({
                  ...f,
                  chapter: e.target.value,
                }))}
                required
              />
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '4fr 1fr',
            gap: '0.75rem',
          }}>
            <div className="form-group">
              <label className="label">Video Title *</label>
              <input
                className="input"
                placeholder="e.g. Indus Valley Civilization - Part 1"
                value={videoForm.title}
                onChange={e => setVideoForm(f => ({
                  ...f,
                  title: e.target.value,
                }))}
                required
              />
            </div>

            <div className="form-group">
              <label className="label">Order #</label>
              <input
                className="input"
                type="number"
                min="0"
                placeholder="0"
                value={videoForm.order}
                onChange={e => setVideoForm(f => ({
                  ...f,
                  order: e.target.value,
                }))}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="label">YouTube URL *</label>
            <input
              className="input"
              placeholder="https://www.youtube.com/watch?v=xxxxxxxx or https://youtu.be/xxxxxxxx"
              value={videoForm.youtubeUrl}
              onChange={e => setVideoForm(f => ({
                ...f,
                youtubeUrl: e.target.value,
              }))}
              required
            />
            <p style={{
              fontSize: '.72rem',
              color: 'var(--text-3)',
              marginTop: '.3rem',
            }}>
              Paste the YouTube link. The video will embed on CivilFox automatically.
            </p>
          </div>

          {ytPreviewId && (
            <div style={{
              background: 'var(--bg-3)',
              borderRadius: 12,
              padding: '1rem',
              border: '1px solid rgba(15,23,42,0.07)',
              display: 'flex',
              gap: '1rem',
              alignItems: 'flex-start',
            }}>
              <img
                src={`https://img.youtube.com/vi/${ytPreviewId}/mqdefault.jpg`}
                alt="thumbnail"
                style={{
                  width: 160,
                  borderRadius: 8,
                  flexShrink: 0,
                  aspectRatio: '16/9',
                  objectFit: 'cover',
                }}
              />
              <div>
                <p style={{
                  margin: '0 0 .3rem',
                  fontSize: '.75rem',
                  color: '#16A34A',
                  fontWeight: 700,
                }}>
                  ✅ Valid YouTube URL detected
                </p>
                <p style={{
                  margin: '0 0 .5rem',
                  fontSize: '.82rem',
                  color: 'var(--text-2)',
                }}>
                  Video ID: <code style={{
                    background: 'rgba(15,23,42,0.06)',
                    padding: '.1rem .4rem',
                    borderRadius: 4,
                  }}>{ytPreviewId}</code>
                </p>
                <a
                  href={`https://www.youtube.com/watch?v=${ytPreviewId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: '.78rem',
                    color: '#ff0000',
                    textDecoration: 'none',
                    fontWeight: 600,
                  }}
                >
                  ▶ Open on YouTube ↗
                </a>
              </div>
            </div>
          )}

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 3fr',
            gap: '0.75rem',
          }}>
            <div className="form-group">
              <label className="label">Duration</label>
              <input
                className="input"
                placeholder="e.g. 18:42"
                value={videoForm.duration}
                onChange={e => setVideoForm(f => ({
                  ...f,
                  duration: e.target.value,
                }))}
              />
            </div>

            <div className="form-group">
              <label className="label">Description (optional)</label>
              <input
                className="input"
                placeholder="Brief note about what this lecture covers"
                value={videoForm.description}
                onChange={e => setVideoForm(f => ({
                  ...f,
                  description: e.target.value,
                }))}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              className="btn btn-primary btn-lg"
              type="submit"
              disabled={saveLoading}
              style={{ flex: 1 }}
            >
              {saveLoading
                ? <><span className="spinner" />Saving...</>
                : editVideo ? '💾 Save Changes' : '➕ Add Video'}
            </button>

            {editVideo && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={cancelEdit}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="admin-card">
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '0.75rem',
          marginBottom: '1rem',
        }}>
          <h3 className="admin-section-title" style={{ margin: 0 }}>
            📋 All Videos ({videoList.length})
          </h3>

          <div style={{ display: 'flex', gap: '0.6rem' }}>
            <select
              className="input"
              style={{ minWidth: 160 }}
              value={filterSub}
              onChange={e => {
                setFilterSub(e.target.value);
                loadVideos(e.target.value);
              }}
            >
              <option value="">All Subjects</option>
              {SUBJECTS.map(s => <option key={s}>{s}</option>)}
            </select>
            <button
              className="btn btn-secondary"
              onClick={() => loadVideos()}
            >
              🔄 Refresh
            </button>
          </div>
        </div>

        {videoLoading ? (
          <div className="spinner" />
        ) : videoList.length === 0 ? (
          <p style={{ color: 'var(--text-3)', fontSize: '.88rem' }}>
            No videos yet. Add your first lecture above.
          </p>
        ) : (
          Object.values(grouped).map(group => (
            <div
              key={`${group.subject}|||${group.chapter}`}
              style={{ marginBottom: '1.5rem' }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.6rem',
                marginBottom: '0.6rem',
                paddingBottom: '0.5rem',
                borderBottom: '1px solid rgba(15,23,42,0.07)',
              }}>
                <span className="badge badge-saffron">{group.subject}</span>
                <span style={{
                  fontWeight: 700,
                  fontSize: '.9rem',
                  color: 'var(--text)',
                }}>{group.chapter}</span>
                <span style={{
                  fontSize: '.75rem',
                  color: 'var(--text-3)',
                }}>
                  ({group.videos.length} video{group.videos.length !== 1 ? 's' : ''})
                </span>
              </div>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.45rem',
                paddingLeft: '0.5rem',
              }}>
                {group.videos.map(v => {
                  const vid = getYouTubeId(v.youtubeUrl);

                  return (
                    <div
                      key={v._id}
                      style={{
                        background: 'var(--bg-3)',
                        borderRadius: 10,
                        padding: '0.75rem 1rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                      }}
                    >
                      {vid ? (
                        <img
                          src={`https://img.youtube.com/vi/${vid}/default.jpg`}
                          alt={v.title}
                          style={{
                            width: 72,
                            height: 54,
                            borderRadius: 6,
                            objectFit: 'cover',
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <div style={{
                          width: 72,
                          height: 54,
                          borderRadius: 6,
                          background: 'rgba(15,23,42,0.05)',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.2rem',
                        }}>🎬</div>
                      )}

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{
                          margin: '0 0 .2rem',
                          fontWeight: 600,
                          fontSize: '.88rem',
                          color: 'var(--text)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {v.title}
                        </p>

                        <div style={{
                          display: 'flex',
                          gap: '0.4rem',
                          flexWrap: 'wrap',
                        }}>
                          {v.duration && (
                            <span className="badge badge-muted">
                              ⏱ {v.duration}
                            </span>
                          )}
                          <span className="badge badge-muted">#{v.order}</span>
                          {vid && (
                            <a
                              href={`https://youtube.com/watch?v=${vid}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                fontSize: '.72rem',
                                color: '#ff0000',
                                textDecoration: 'none',
                                fontWeight: 600,
                              }}
                            >
                              ▶ YT
                            </a>
                          )}
                        </div>
                      </div>

                      <div style={{
                        display: 'flex',
                        gap: '0.4rem',
                        flexShrink: 0,
                      }}>
                        <button
                          onClick={() => startEdit(v)}
                          style={{
                            background: 'rgba(79,142,247,0.1)',
                            border: '1px solid rgba(79,142,247,0.25)',
                            color: '#0056D2',
                            padding: '.35rem .7rem',
                            borderRadius: 8,
                            cursor: 'pointer',
                            fontSize: '.78rem',
                            fontWeight: 600,
                          }}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => deleteVideo(v._id)}
                          style={{
                            background: 'rgba(220,38,38,0.1)',
                            border: '1px solid rgba(220,38,38,0.25)',
                            color: '#DC2626',
                            padding: '.35rem .7rem',
                            borderRadius: 8,
                            cursor: 'pointer',
                            fontSize: '.78rem',
                            fontWeight: 600,
                          }}
                        >
                          🗑
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* ─── Main Admin Panel ──────────────────────────────────────────────────── */
export default function AdminPanel() {
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('success');
  const [loading, setLoading] = useState(false);
  const [topicCards, setTopicCards] = useState([]);
  const [topicForm, setTopicForm] = useState({
    topic: '',
    subject: '',
    examType: '',
  });

  const [pyqForm, setPyqForm] = useState(EMPTY_PYQ);
  const [pyqList, setPyqList] = useState([]);
  const [pyqTotal, setPyqTotal] = useState(0);
  const [pyqPage, setPyqPage] = useState(1);
  const [pyqFilter, setPyqFilter] = useState({
    exam: '',
    subject: '',
    q: '',
  });
  const [pyqLoading, setPyqLoading] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  const [cardForm, setCardForm] = useState({
    question: '',
    answer: '',
    explanation: '',
    subject: 'Polity',
    examType: 'Prelims',
    questionType: 'MCQ',
    difficulty: 'Medium',
    mainsGSPaper: null,
    tags: '',
    hints: '',
    options: [
      { text: '', isCorrect: true },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
    ],
  });

  const showMsg = (text, type = 'success') => {
    setMsg(text);
    setMsgType(type);
    setTimeout(() => setMsg(''), 5000);
  };

  useEffect(() => {
    if (tab === 'overview') {
      adminAPI.getStats()
        .then(r => setStats(r.data.stats))
        .catch(console.error);
    }

    if (tab === 'users') {
      adminAPI.getUsers()
        .then(r => setUsers(r.data.users || []))
        .catch(console.error);
    }

    if (tab === 'pyq') loadPYQ();
  }, [tab]); // eslint-disable-line

  const loadPYQ = async (page = 1) => {
    setPyqLoading(true);
    try {
      const res = await api.get('/admin/exam-questions', {
        params: { page, limit: 15, ...pyqFilter },
      });
      setPyqList(res.data.questions || []);
      setPyqTotal(res.data.total || 0);
      setPyqPage(page);
    } catch {
      showMsg('❌ Failed to load questions', 'error');
    } finally {
      setPyqLoading(false);
    }
  };

  const savePYQ = async (e) => {
    e.preventDefault();
    if (pyqForm.questionType === 'mains' && isRichEmpty(pyqForm.question)) return showMsg('❌ Please write the question', 'error');
    if (pyqForm.questionType === 'mains' && isRichEmpty(pyqForm.answer)) return showMsg('❌ Please write the model answer', 'error');
    setLoading(true);

    try {
      await api.post('/admin/exam-questions', buildPYQPayload(pyqForm));
      showMsg('✅ Question saved to PYQ Bank!');
      setPyqForm(EMPTY_PYQ);
      loadPYQ(1);
    } catch (err) {
      showMsg('❌ ' + (err.response?.data?.message || 'Save failed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (q) => {
    setEditingId(q._id);
    setEditForm(questionToForm(q));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeEdit = () => {
    setEditingId(null);
    setEditForm(null);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (editForm.questionType === 'mains' && isRichEmpty(editForm.question)) return showMsg('❌ Please write the question', 'error');
    if (editForm.questionType === 'mains' && isRichEmpty(editForm.answer)) return showMsg('❌ Please write the model answer', 'error');    setEditLoading(true);

    try {
      await api.put(
        `/admin/exam-questions/${editingId}`,
        buildPYQPayload(editForm)
      );
      showMsg('✅ Question updated!');
      closeEdit();
      loadPYQ(pyqPage);
    } catch (err) {
      showMsg(
        '❌ ' + (err.response?.data?.message || 'Update failed'),
        'error'
      );
    } finally {
      setEditLoading(false);
    }
  };

  const updatePyqOpt = (i, val) => setPyqForm(f => {
    const opts = [...f.options];
    opts[i] = { ...opts[i], text: val };
    return { ...f, options: opts };
  });

  const updateEditOpt = (i, val) => setEditForm(f => {
    const opts = [...f.options];
    opts[i] = { ...opts[i], text: val };
    return { ...f, options: opts };
  });

  const deletePYQ = async (id) => {
    if (!window.confirm('Delete this question?')) return;

    try {
      await api.delete(`/admin/exam-questions/${id}`);
      showMsg('✅ Deleted');
      loadPYQ(pyqPage);
    } catch {
      showMsg('❌ Delete failed', 'error');
    }
  };

  const runAction = async (fn, successMsg) => {
    setLoading(true);

    try {
      await fn();
      showMsg(successMsg);
    } catch (err) {
      showMsg('❌ ' + (err.response?.data?.message || 'Failed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const generateTopic = async (e) => {
    e.preventDefault();
    if (!topicForm.topic) return;

    setLoading(true);
    setTopicCards([]);

    try {
      const res = await newsAPI.generateTopic(topicForm);
      setTopicCards(res.data.cards || []);
      showMsg(`✅ ${res.data.cards?.length || 0} cards generated`);
    } catch (err) {
      showMsg('❌ ' + (err.response?.data?.message || 'Failed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const saveCard = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await adminAPI.addCard({
        ...cardForm,
        tags: cardForm.tags.split(',').map(t => t.trim()).filter(Boolean),
        hints: cardForm.hints.split(',').map(h => h.trim()).filter(Boolean),
        options: cardForm.questionType === 'MCQ' ? cardForm.options : [],
        mainsGSPaper: cardForm.examType === 'Mains' ? cardForm.mainsGSPaper : null,
      });

      showMsg('✅ Card saved!');
      setCardForm(f => ({
        ...f,
        question: '',
        answer: '',
        explanation: '',
        tags: '',
        hints: '',
      }));
    } catch (err) {
      showMsg('❌ ' + (err.response?.data?.message || 'Save failed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const updateOpt = (i, field, val) => {
    setCardForm(f => {
      const opts = [...f.options];

      if (field === 'isCorrect') {
        opts.forEach((o, j) => {
          o.isCorrect = j === i;
        });
      } else {
        opts[i] = { ...opts[i], [field]: val };
      }

      return { ...f, options: opts };
    });
  };

  const qtBadge = (qt) => ({
    mcq: 'MCQ',
    statement: 'Statement',
    'assertion-reason': 'A/R',
    'match-list': '🔀 Match',
    mains: '✍️ Mains',
  }[qt] || qt);

  return (
    <div className="admin-panel fade-in">
      <div>
        <h1 className="page-title">🔧 Admin Panel</h1>
        <p className="page-sub">Manage cards, users, news and content</p>
      </div>

      {msg && (
        <div className={`admin-msg ${msgType === 'success' ? 'success' : 'error'}`}>
          {msg}
        </div>
      )}

      <div className="admin-tabs">
        {[
          ['overview', '📊 Overview'],
          ['generate', '⚡ Generate'],
          ['cards', '🃏 Add Card'],
          ['pyq', '📚 PYQ Bank'],
          ['syllabus', '🎬 Syllabus'],
          ['users', '👥 Users'],
        ].map(([k, l]) => (
          <button
            key={k}
            className={`ui-tab ${tab === k ? 'active' : ''}`}
            onClick={() => {
              setTab(k);
              closeEdit();
            }}
          >
            {l}
          </button>
        ))}
      </div>

      {/* ── Overview ── */}
      {tab === 'overview' && (
        <div className="admin-section slide-up">
          {!stats ? (
            <div className="spinner" />
          ) : (
            <>
              <div className="admin-stats-grid">
                {[
                  ['👥 Total Users', stats.totalUsers, '#0056D2'],
                  ['🟢 Active Today', stats.activeToday, '#16A34A'],
                  ['📅 This Week', stats.activeWeek, '#F59E0B'],
                  ['🆕 New Today', stats.newUsersToday, '#0056D2'],
                  ['📚 Total Cards', stats.totalCards, '#0056D2'],
                  ['📰 Cards Today', stats.cardsToday, '#16A34A'],
                  ['🔁 Total Reviews', stats.totalReviews, '#F59E0B'],
                ].map(([label, val, color]) => (
                  <div
                    key={label}
                    className="admin-stat-card"
                    style={{ '--as-color': color }}
                  >
                    <div className="as-label">{label}</div>
                    <div className="as-val">
                      {(val || 0).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>

              <div className="admin-card">
                <h3 className="admin-section-title">Cards by Subject</h3>
                <div className="subject-bars">
                  {stats.cardsBySubject?.map(s => {
                    const pct = stats.totalCards > 0
                      ? Math.round((s.count / stats.totalCards) * 100)
                      : 0;

                    return (
                      <div key={s._id} className="subject-bar-row">
                        <span className="sb-name">{s._id}</span>
                        <div className="sb-bar">
                          <div
                            className="sb-fill"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="sb-count">{s.count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="admin-card">
                <h3 className="admin-section-title">Recent Sign-ups</h3>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Streak</th>
                      <th>Studied</th>
                      <th>Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recentUsers?.map(u => (
                      <tr key={u._id}>
                        <td>{u.name}</td>
                        <td style={{ fontSize: '.8rem', color: 'var(--text-2)' }}>
                          {u.email}
                        </td>
                        <td>
                          <span className={`badge ${u.role === 'admin' ? 'badge-saffron' : 'badge-muted'}`}>
                            {u.role}
                          </span>
                        </td>
                        <td>🔥 {u.studyStreak}</td>
                        <td>{u.totalCardsStudied}</td>
                        <td style={{ fontSize: '.75rem', color: 'var(--text-3)' }}>
                          {new Date(u.createdAt).toLocaleDateString('en-IN')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Generate ── */}
      {tab === 'generate' && (
        <div className="admin-section slide-up">
          <div className="admin-card">
            <h3 className="admin-section-title">⚙️ System Actions</h3>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button
                className="btn btn-secondary"
                disabled={loading}
                onClick={() => runAction(
                  () => newsAPI.triggerFetch(),
                  '✅ News fetch started'
                )}
              >
                📰 Fetch News Now
              </button>
              <button
                className="btn btn-secondary"
                disabled={loading}
                onClick={() => runAction(
                  () => newsAPI.triggerExplain(),
                  '✅ Explanations started'
                )}
              >
                🎓 Generate Explanations
              </button>
              <button
                className="btn btn-primary"
                disabled={loading}
                onClick={() => runAction(
                  () => newsAPI.generate(),
                  '✅ Card generation started'
                )}
              >
                🚀 Generate Cards Now
              </button>
            </div>
          </div>

          <div className="admin-card">
            <h3 className="admin-section-title">🎯 Generate from Custom Topic</h3>
            <form
              onSubmit={generateTopic}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '.85rem',
              }}
            >
              <div className="form-group">
                <label className="label">Topic / News Headline *</label>
                <textarea
                  className="input"
                  rows={2}
                  placeholder="e.g. India's Digital Personal Data Protection Act 2023"
                  value={topicForm.topic}
                  onChange={e => setTopicForm(f => ({
                    ...f,
                    topic: e.target.value,
                  }))}
                  required
                />
              </div>

              <div style={{
                display: 'flex',
                gap: '0.75rem',
                flexWrap: 'wrap',
              }}>
                <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
                  <label className="label">Subject</label>
                  <select
                    className="input"
                    value={topicForm.subject}
                    onChange={e => setTopicForm(f => ({
                      ...f,
                      subject: e.target.value,
                    }))}
                  >
                    <option value="">Auto-detect</option>
                    {SUBJECTS.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>

                <div className="form-group" style={{ flex: 1, minWidth: 140 }}>
                  <label className="label">Exam Focus</label>
                  <select
                    className="input"
                    value={topicForm.examType}
                    onChange={e => setTopicForm(f => ({
                      ...f,
                      examType: e.target.value,
                    }))}
                  >
                    <option value="">Both</option>
                    <option>Prelims</option>
                    <option>Mains</option>
                  </select>
                </div>
              </div>

              <button
                className="btn btn-primary"
                type="submit"
                disabled={loading}
              >
                {loading
                  ? <><span className="spinner" />Generating...</>
                  : '✨ Generate 5 Cards'}
              </button>
            </form>

            {topicCards.length > 0 && (
              <div className="topic-cards-result">
                {topicCards.map((c, i) => (
                  <div
                    key={c._id || i}
                    className="topic-card-preview"
                  >
                    <div className="tcp-badges">
                      {c.subject && (
                        <span className="badge badge-saffron">{c.subject}</span>
                      )}
                      {c.examType && (
                        <span className="badge badge-muted">{c.examType}</span>
                      )}
                      {c.questionType && (
                        <span className="badge badge-blue">{c.questionType}</span>
                      )}
                    </div>
                    <p className="tcp-q">{c.question}</p>
                    <p className="tcp-a">{c.answer}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Add Card ── */}
      {tab === 'cards' && (
        <div className="admin-section slide-up">
          <div className="admin-card">
            <h3 className="admin-section-title">Add Flashcard Manually</h3>

            <form
              onSubmit={saveCard}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '.85rem',
              }}
            >
              <div className="form-group">
                <label className="label">Question *</label>
                <textarea
                  className="input"
                  rows={3}
                  value={cardForm.question}
                  onChange={e => setCardForm(f => ({
                    ...f,
                    question: e.target.value,
                  }))}
                  required
                />
              </div>

              <div style={{
                display: 'flex',
                gap: '.75rem',
                flexWrap: 'wrap',
              }}>
                <div className="form-group" style={{ flex: 1, minWidth: 140 }}>
                  <label className="label">Subject *</label>
                  <select
                    className="input"
                    value={cardForm.subject}
                    onChange={e => setCardForm(f => ({
                      ...f,
                      subject: e.target.value,
                    }))}
                  >
                    {SUBJECTS.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>

                <div className="form-group" style={{ flex: 1, minWidth: 120 }}>
                  <label className="label">Exam Type</label>
                  <select
                    className="input"
                    value={cardForm.examType}
                    onChange={e => setCardForm(f => ({
                      ...f,
                      examType: e.target.value,
                    }))}
                  >
                    <option>Prelims</option>
                    <option>Mains</option>
                    <option>Both</option>
                  </select>
                </div>

                <div className="form-group" style={{ flex: 1, minWidth: 140 }}>
                  <label className="label">Question Type</label>
                  <select
                    className="input"
                    value={cardForm.questionType}
                    onChange={e => setCardForm(f => ({
                      ...f,
                      questionType: e.target.value,
                    }))}
                  >
                    <option>MCQ</option>
                    <option>Direct</option>
                    <option>Statement-Based</option>
                    <option>Assertion-Reason</option>
                  </select>
                </div>
              </div>

              {cardForm.questionType === 'MCQ' && (
                <div className="form-group">
                  <label className="label">Options</label>
                  {cardForm.options.map((opt, i) => (
                    <div key={i} className="option-row">
                      <input
                        type="radio"
                        name="correct"
                        checked={opt.isCorrect}
                        onChange={() => updateOpt(i, 'isCorrect', true)}
                      />
                      <span className="opt-letter">
                        {String.fromCharCode(65 + i)}
                      </span>
                      <input
                        className="input"
                        style={{ flex: 1 }}
                        placeholder={`Option ${String.fromCharCode(65 + i)}`}
                        value={opt.text}
                        onChange={e => updateOpt(i, 'text', e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              )}

              <div className="form-group">
                <label className="label">Answer *</label>
                <textarea
                  className="input"
                  rows={4}
                  value={cardForm.answer}
                  onChange={e => setCardForm(f => ({
                    ...f,
                    answer: e.target.value,
                  }))}
                  required
                />
              </div>

              <div className="form-group">
                <label className="label">Explanation</label>
                <textarea
                  className="input"
                  rows={3}
                  value={cardForm.explanation}
                  onChange={e => setCardForm(f => ({
                    ...f,
                    explanation: e.target.value,
                  }))}
                />
              </div>

              <button
                className="btn btn-primary btn-lg"
                type="submit"
                disabled={loading}
              >
                {loading ? <><span className="spinner" />Saving...</> : '💾 Save Card'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── PYQ Bank ── */}
      {tab === 'pyq' && (
        <div className="admin-section slide-up">
          {editingId && editForm ? (
            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '1.5rem',
                paddingBottom: '1rem',
                borderBottom: '1px solid rgba(15,23,42,0.07)',
              }}>
                <button
                  className="btn btn-secondary"
                  onClick={closeEdit}
                >
                  ← Back to PYQ Bank
                </button>
                <div>
                  <h2 style={{
                    margin: 0,
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    color: 'var(--text)',
                  }}>
                    ✏️ Edit Question
                  </h2>
                  <p style={{
                    margin: 0,
                    fontSize: '.78rem',
                    color: 'var(--text-3)',
                  }}>
                    ID: {editingId}
                  </p>
                </div>
              </div>

              <div className="admin-card">
                <PYQFormFields
                  form={editForm}
                  setForm={setEditForm}
                  updateOptFn={updateEditOpt}
                  onSubmit={saveEdit}
                  submitLabel="💾 Save Changes"
                  isLoading={editLoading}
                />
                <button
                  onClick={closeEdit}
                  style={{
                    marginTop: '.75rem',
                    width: '100%',
                    padding: '.6rem',
                    background: 'transparent',
                    border: '1px solid rgba(15,23,42,0.08)',
                    borderRadius: 10,
                    color: 'var(--text-3)',
                    cursor: 'pointer',
                    fontSize: '.88rem',
                  }}
                >
                  Cancel — discard changes
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="admin-card">
                <h3 className="admin-section-title">
                  📥 Add Past Exam Question
                </h3>

                <PYQFormFields
                  form={pyqForm}
                  setForm={setPyqForm}
                  updateOptFn={updatePyqOpt}
                  onSubmit={savePYQ}
                  submitLabel="💾 Add to PYQ Bank"
                  isLoading={loading}
                />
              </div>

              <div className="admin-card">
                <h3 className="admin-section-title">
                  📋 Existing Questions ({pyqTotal})
                </h3>

                <div style={{
                  display: 'flex',
                  gap: '.6rem',
                  flexWrap: 'wrap',
                  marginBottom: '1rem',
                }}>
                  <input
                    className="input"
                    style={{ flex: 2, minWidth: 160 }}
                    placeholder="Search keyword..."
                    value={pyqFilter.q}
                    onChange={e => setPyqFilter(f => ({
                      ...f,
                      q: e.target.value,
                    }))}
                  />

                  <select
                    className="input"
                    style={{ flex: 1, minWidth: 130 }}
                    value={pyqFilter.exam}
                    onChange={e => setPyqFilter(f => ({
                      ...f,
                      exam: e.target.value,
                    }))}
                  >
                    <option value="">All Exams</option>
                    {EXAMS.map(ex => <option key={ex}>{ex}</option>)}
                  </select>

                  <select
                    className="input"
                    style={{ flex: 1, minWidth: 130 }}
                    value={pyqFilter.subject}
                    onChange={e => setPyqFilter(f => ({
                      ...f,
                      subject: e.target.value,
                    }))}
                  >
                    <option value="">All Subjects</option>
                    {SUBJECTS.map(s => <option key={s}>{s}</option>)}
                  </select>

                  <button
                    className="btn btn-secondary"
                    onClick={() => loadPYQ(1)}
                  >
                    🔍 Filter
                  </button>
                </div>

                {pyqLoading ? (
                  <div className="spinner" />
                ) : (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '.55rem',
                  }}>
                    {pyqList.length === 0 && (
                      <p style={{
                        color: 'var(--text-3)',
                        fontSize: '.88rem',
                      }}>
                        No questions yet.
                      </p>
                    )}

                    {pyqList.map(q => (
                      <div
                        key={q._id}
                        style={{
                          background: 'var(--bg-3)',
                          borderRadius: 10,
                          padding: '.9rem 1rem',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: '1rem',
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            display: 'flex',
                            gap: '.4rem',
                            flexWrap: 'wrap',
                            marginBottom: '.4rem',
                          }}>
                            <span className="badge badge-saffron">{q.exam}</span>
                            {q.year && (
                              <span className="badge badge-muted">{q.year}</span>
                            )}
                            <span className="badge badge-muted">{q.subject}</span>
                            <span className="badge badge-blue">
                              {qtBadge(q.questionType)}
                            </span>
                            {q.difficulty && (
                              <span
                                className="badge badge-muted"
                                style={{ textTransform: 'capitalize' }}
                              >
                                {q.difficulty}
                              </span>
                            )}
                          </div>

                          <p style={{
                            fontSize: '.85rem',
                            color: 'var(--text)',
                            margin: 0,
                            lineHeight: 1.6,
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}>
                            {q.question}
                          </p>
                        </div>

                        <div style={{
                          display: 'flex',
                          gap: '.4rem',
                          flexShrink: 0,
                        }}>
                          <button
                            onClick={() => openEdit(q)}
                            style={{
                              background: 'rgba(79,142,247,0.1)',
                              border: '1px solid rgba(79,142,247,0.25)',
                              color: '#0056D2',
                              padding: '.38rem .75rem',
                              borderRadius: 8,
                              cursor: 'pointer',
                              fontSize: '.78rem',
                              fontWeight: 600,
                            }}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => deletePYQ(q._id)}
                            style={{
                              background: 'rgba(220,38,38,0.1)',
                              border: '1px solid rgba(220,38,38,0.25)',
                              color: '#DC2626',
                              padding: '.38rem .75rem',
                              borderRadius: 8,
                              cursor: 'pointer',
                              fontSize: '.78rem',
                              fontWeight: 600,
                            }}
                          >
                            🗑
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {pyqTotal > 15 && (
                  <div style={{
                    display: 'flex',
                    gap: '.5rem',
                    justifyContent: 'center',
                    marginTop: '1rem',
                  }}>
                    <button
                      className="btn btn-secondary"
                      disabled={pyqPage === 1}
                      onClick={() => loadPYQ(pyqPage - 1)}
                    >
                      ← Prev
                    </button>

                    <span style={{
                      color: 'var(--text-2)',
                      padding: '.5rem',
                      fontSize: '.85rem',
                    }}>
                      {pyqPage} / {Math.ceil(pyqTotal / 15)}
                    </span>

                    <button
                      className="btn btn-secondary"
                      disabled={pyqPage >= Math.ceil(pyqTotal / 15)}
                      onClick={() => loadPYQ(pyqPage + 1)}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Syllabus (Video Lectures) ── */}
      {tab === 'syllabus' && (
        <div className="admin-section slide-up">
          <SyllabusManager showMsg={showMsg} />
        </div>
      )}

      {/* ── Users ── */}
      {tab === 'users' && (
        <div className="admin-section slide-up">
          <div className="admin-card">
            <h3 className="admin-section-title">
              All Users ({users.length})
            </h3>

            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Streak</th>
                  <th>Cards</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u._id}>
                    <td>{u.name}</td>
                    <td style={{ fontSize: '.8rem', color: 'var(--text-2)' }}>
                      {u.email}
                    </td>
                    <td>
                      <span className={`badge ${u.role === 'admin' ? 'badge-saffron' : 'badge-muted'}`}>
                        {u.role}
                      </span>
                    </td>
                    <td>🔥 {u.studyStreak}</td>
                    <td>{u.totalCardsStudied}</td>
                    <td style={{ fontSize: '.75rem', color: 'var(--text-3)' }}>
                      {new Date(u.createdAt).toLocaleDateString('en-IN')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}